<template>
  <div class="register-container">
    <div class="register-box">
      <h2 class="register-title">注册账号</h2>

      <!-- 用户名：必填，6-14位字母数字组合 -->
      <div class="form-item">
        <label><span class="required">*</span>用户名</label>
        <input
          v-model="form.userName"
          type="text"
          placeholder="6-14位字母或数字"
          :class="{ 'input-error': errors.userName }"
          @input="errors.userName = ''"
        />
        <div v-if="errors.userName" class="field-error">{{ errors.userName }}</div>
      </div>

      <!-- 密码：必填，外层相对定位，右侧放“小眼睛”按钮切换明文/密文 -->
      <div class="form-item">
        <label><span class="required">*</span>密码</label>
        <div class="password-wrap">
          <input
            v-model="form.userPassword"
            :type="showPassword ? 'text' : 'password'"
            placeholder="请输入密码"
            :class="{ 'input-error': errors.userPassword }"
            @input="errors.userPassword = ''"
          />
          <!-- 点击切换密码是否可见 -->
          <span class="password-eye" @click="showPassword = !showPassword">
            <!-- 可见状态：睁眼图标 -->
            <svg v-if="showPassword" viewBox="0 0 24 24">
              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <!-- 隐藏状态：闭眼图标（眼睛上加一道斜线） -->
            <svg v-else viewBox="0 0 24 24">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-7 0-11-7-11-7a19.77 19.77 0 0 1 4.22-5.06" />
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a19.87 19.87 0 0 1-3.17 4.19" />
              <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </span>
        </div>
        <div v-if="errors.userPassword" class="field-error">{{ errors.userPassword }}</div>
      </div>

      <!-- 确认密码：必填，需与密码一致（仅前端校验，不提交后端），同样带“小眼睛” -->
      <div class="form-item">
        <label><span class="required">*</span>确认密码</label>
        <div class="password-wrap">
          <input
            v-model="form.confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            placeholder="请再次输入密码"
            :class="{ 'input-error': errors.confirmPassword }"
            @input="errors.confirmPassword = ''"
          />
          <!-- 点击切换确认密码是否可见 -->
          <span class="password-eye" @click="showConfirmPassword = !showConfirmPassword">
            <!-- 可见状态：睁眼图标 -->
            <svg v-if="showConfirmPassword" viewBox="0 0 24 24">
              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <!-- 隐藏状态：闭眼图标（眼睛上加一道斜线） -->
            <svg v-else viewBox="0 0 24 24">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-7 0-11-7-11-7a19.77 19.77 0 0 1 4.22-5.06" />
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a19.87 19.87 0 0 1-3.17 4.19" />
              <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          </span>
        </div>
        <div v-if="errors.confirmPassword" class="field-error">{{ errors.confirmPassword }}</div>
      </div>

      <!-- 后端返回的错误提示 -->
      <div v-if="errorMsg" class="error-msg">{{ errorMsg }}</div>

      <button class="register-btn" :disabled="loading" @click="handleRegister">
        {{ loading ? '注册中...' : '注 册' }}
      </button>

      <div class="login-link" @click="goLogin">已有账号？返回登录</div>
    </div>
  </div>
</template>

<script>
import { register } from '@/api/user'

export default {
  name: 'RegisterView',
  data() {
    return {
      // 表单数据：userName、userPassword 为必填
      form: {
        userName: '',
        userPassword: '',
        confirmPassword: ''
      },
      // 每个字段各自的错误提示
      errors: {
        userName: '',
        userPassword: '',
        confirmPassword: ''
      },
      loading: false,
      errorMsg: '',
      showPassword: false,        // 密码是否明文显示（小眼睛切换）
      showConfirmPassword: false  // 确认密码是否明文显示（小眼睛切换）
    }
  },
  methods: {
    // 表单校验：全部通过返回 true
    validate() {
      let valid = true

      // 用户名：必填，6-14位字母数字组合
      if (!this.form.userName) {
        this.errors.userName = '请输入用户名'
        valid = false
      } else if (!/^[A-Za-z0-9]{6,14}$/.test(this.form.userName)) {
        this.errors.userName = '用户名需为6-14位字母或数字组合'
        valid = false
      }

      // 密码：必填
      if (!this.form.userPassword) {
        this.errors.userPassword = '请输入密码'
        valid = false
      }

      // 确认密码：必填，且要与密码一致
      if (!this.form.confirmPassword) {
        this.errors.confirmPassword = '请再次输入密码'
        valid = false
      } else if (this.form.confirmPassword !== this.form.userPassword) {
        this.errors.confirmPassword = '两次输入的密码不一致'
        valid = false
      }

      return valid
    },

    // 点击注册
    async handleRegister() {
      // 先做前端校验，不通过则不发请求
      if (!this.validate()) {
        return
      }

      this.loading = true
      this.errorMsg = ''

      // 提交给后端的数据：确认密码仅用于前端校验，不传后端
      const payload = {
        userName: this.form.userName,
        userPassword: this.form.userPassword
      }

      try {
        const res = await register(payload)
        // 后端返回格式：{ code: "200", data: "xxx", msg: null, success: true }
        if (res.code === '200' || res.success) {
          alert('注册成功，请登录')
          this.$router.push('/login')
        } else {
          // 注册失败（如用户名已存在），直接展示后端返回的提示
          this.errorMsg = res.msg || '注册失败，请重试'
        }
      } catch (err) {
        this.errorMsg = '网络错误，请稍后重试'
        console.error(err)
      } finally {
        this.loading = false
      }
    },

    // 返回登录页
    goLogin() {
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.register-container {
  width: 100%;
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  /* 多层渐变叠加：底层斜向三色渐变 + 径向光晕营造层次感 */
  background:
      radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.12) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(118, 75, 162, 0.4) 0%, transparent 50%),
      linear-gradient(135deg, #667eea 0%, #6a8de0 35%, #764ba2 100%);
}

.register-box {
  width: 100%;
  max-width: 400px;
  padding: 36px 30px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(102, 126, 234, 0.1);
}

.register-title {
  text-align: center;
  margin-bottom: 26px;
  color: #333;
  font-size: 24px;
}

.form-item {
  margin-bottom: 16px;
}

.form-item label {
  display: block;
  margin-bottom: 8px;
  color: #606266;
  font-size: 14px;
}

/* 必填星号 */
.required {
  color: #f56c6c;
  margin-right: 2px;
}

.form-item input {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.form-item input:focus {
  border-color: #667eea;
}

/* 校验不通过时输入框变红 */
.form-item input.input-error {
  border-color: #f56c6c;
}

/* 密码输入框外层：相对定位，作为眼睛按钮的定位参照 */
.password-wrap {
  position: relative;
}

/* 密码框右侧留出眼睛图标的位置，避免文字被挡住 */
.password-wrap input {
  padding-right: 38px;
}

/* 眼睛按钮：绝对定位在输入框右侧垂直居中 */
.password-eye {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.password-eye svg {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: #909399;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* 字段级错误提示 */
.field-error {
  margin-top: 4px;
  color: #f56c6c;
  font-size: 12px;
}

/* 后端返回的整体错误提示 */
.error-msg {
  margin-bottom: 16px;
  color: #f56c6c;
  font-size: 13px;
  text-align: center;
}

.register-btn {
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.register-btn:hover {
  opacity: 0.9;
}

.register-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-link {
  margin-top: 16px;
  text-align: center;
  color: #667eea;
  font-size: 13px;
  cursor: pointer;
}

.login-link:hover {
  text-decoration: underline;
}

/* 手机端适配 */
@media screen and (max-width: 480px) {
  .register-box {
    margin: 0 16px;
    padding: 26px 20px;
  }

  .form-item input {
    font-size: 16px; /* 防止 iPhone 聚焦时页面放大 */
  }
}
</style>
